import { peekBitrix24GuestTrace, readBitrix24GuestTrace } from "@/lib/bitrix24/tracking";
import { readStoredUtm } from "@/lib/analytics/utm";
import {
  isInternalJourneyPath,
  parseBitrixTrace,
} from "@/lib/attribution/trace";

const CAPTURE_TIMEOUT_MS = 4000;
const FP_KEY = "idealitika:b24-trace-fp:v1";

async function postAttribution(body: Record<string, unknown>): Promise<void> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), CAPTURE_TIMEOUT_MS);
  try {
    await fetch("/api/attribution/capture", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      keepalive: true,
      signal: controller.signal,
      body: JSON.stringify(body),
    });
  } catch {
    /* аналитика не блокирует UX */
  } finally {
    window.clearTimeout(timer);
  }
}

function pageMeta() {
  return {
    landingPath: `${window.location.pathname}${window.location.search}`.slice(
      0,
      500,
    ),
    referrer: document.referrer?.trim().slice(0, 1000) || undefined,
    utm: readStoredUtm(),
  };
}

function readStoredFingerprint(): string | null {
  try {
    return sessionStorage.getItem(FP_KEY);
  } catch {
    return null;
  }
}

function writeStoredFingerprint(fp: string): void {
  try {
    sessionStorage.setItem(FP_KEY, fp);
  } catch {
    /* private mode */
  }
}

/**
 * Сохранить снимок трейса Bitrix, только если путь клиента в B24 изменился.
 * getTrace не вызываем — remindTrace не очищает накопленный путь.
 */
export async function syncBitrixTraceSnapshot(
  maxWaitMs = 2000,
): Promise<void> {
  if (typeof window === "undefined") return;
  if (isInternalJourneyPath(window.location.pathname)) return;

  const started = Date.now();
  let trace = peekBitrix24GuestTrace();
  let parsed = parseBitrixTrace(trace);
  while (!parsed && Date.now() - started < maxWaitMs) {
    await new Promise((resolve) => window.setTimeout(resolve, 250));
    trace = peekBitrix24GuestTrace();
    parsed = parseBitrixTrace(trace);
  }
  if (!trace || !parsed) return;
  if (readStoredFingerprint() === parsed.fingerprint) return;
  writeStoredFingerprint(parsed.fingerprint);

  await postAttribution({
    channel: "web",
    kind: "pageview",
    trace,
    ...pageMeta(),
  });
}

/** Снять трейс B24 (getTrace очищает) до редиректа на OAuth. */
export async function captureAttributionBeforeOAuth(
  channel: "yandex" | "vk",
): Promise<void> {
  if (typeof window === "undefined") return;
  const peeked = peekBitrix24GuestTrace();
  const consumed = readBitrix24GuestTrace();
  const trace = consumed ?? peeked;
  if (trace) {
    const parsed = parseBitrixTrace(trace);
    if (parsed) writeStoredFingerprint(parsed.fingerprint);
  }
  await postAttribution({
    channel,
    kind: "touch",
    trace,
    ...pageMeta(),
  });
}
